import { type ReactNode, useCallback, useContext, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { matchPath, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import { ROUTES, SUPPRESSED_AUTH_ERROR_ROUTES } from '@constants/routes';
import useApp from '@hooks/useApp';
import { useKeycloak } from '@hooks/useKeycloak';
import MainLayout from '@layouts/MainLayout';
import { HcpReferral } from '@pages/bridgeBioReferral/HcpReferral';
import { FvtFamily } from '@pages/fvtFamily/FvtFamily';
import { FvtInvite } from '@pages/fvtInvite/FvtInvite';
import { WizardRouteGuard } from '@pages/order/components/WizardRouteGuard';
import OrderCheckEligibility from '@pages/order/OrderCheckEligibility';
import { OrderGetStarted } from '@pages/order/OrderGetStarted';
import { OrderLayout } from '@pages/order/OrderLayout';
import { OrderNewTest } from '@pages/order/OrderNewTest';
import { OrderNotEligible } from '@pages/order/OrderNotEligible';
import { OrderProvider } from '@pages/order/OrderProvider';
import { OrderWhatsNext } from '@pages/order/OrderWhatsNext';
import { OrderWizard } from '@pages/order/OrderWizard';
import { OrderLaunch } from '@pages/order/pages/Launch/OrderLaunch';
import { ReferralRoot } from '@pages/referrals/ReferralRoot';
import { fetchGeneticCounselingDetails, fetchUserID, fetchUserInfo, fetchUserTestRequests } from '@store/actions';
import { Context } from '@store/store';
import { isFvtOnlyProbandBlockedPath } from '@utils/fvtNav';
import { identifyUser } from '@utils/openreplay';

import routes from './routes';

function RedirectToGetStarted() {
  const { search } = useLocation();

  return <Navigate to={{ pathname: ROUTES.ORDER_GET_STARTED, search }} replace />;
}

function RedirectPartnershipsToPartnership() {
  const { pathname, search } = useLocation();
  const newPath = pathname.replace(/^\/partnerships\//, '/partnership/');
  return <Navigate to={{ pathname: newPath, search }} replace />;
}

function getOrderRoutes(indexElement: ReactNode) {
  return (
    <>
      <Route index element={indexElement} />
      <Route path={ROUTES.ORDER_GET_STARTED} element={<OrderGetStarted />} />
      <Route path={ROUTES.ORDER_CHECK} element={<OrderCheckEligibility />} />
      <Route
        path={ROUTES.ORDER_WIZARD}
        element={
          <WizardRouteGuard>
            <OrderWizard />
          </WizardRouteGuard>
        }
      />
      <Route path={ROUTES.ORDER_WHATS_NEXT} element={<OrderWhatsNext />} />
      <Route path={ROUTES.ORDER_NOT_ELIGIBLE} element={<OrderNotEligible />} />
    </>
  );
}

interface FvtRouteGuardProps {
  fvtReferralUUID: string | null;
  isLoggedIn: boolean;
  isLoadingUserInfo: boolean;
}

// Access gate for the FVT routes: loader while the profile resolves (so a direct hit/refresh
// never flashes the 404 catch-all), then redirect ineligible visitors.
function FvtRouteGuard({ fvtReferralUUID, isLoggedIn, isLoadingUserInfo }: FvtRouteGuardProps) {
  if (isLoadingUserInfo) {
    return (
      <div className='page_loading_spinner_wrapper text-primary'>
        <Spinner animation='border' />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!fvtReferralUUID) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <Outlet />;
}

function FvtOnlyProbandRedirect({ children }: { children: ReactNode }) {
  const { isFvtOnlyProband, isLoadingUserInfo } = useApp();

  if (isLoadingUserInfo) {
    return (
      <div className='page_loading_spinner_wrapper text-primary'>
        <Spinner animation='border' />
      </div>
    );
  }

  if (isFvtOnlyProband) {
    return <Navigate to={ROUTES.FVT_FAMILY} replace />;
  }

  return children;
}

function renderMainLayoutRouteElement(route: (typeof routes)[number], isLoggedIn: boolean) {
  if (route.auth && !isLoggedIn) {
    return <Navigate to={route.redirect ?? '/login'} replace={true} />;
  }

  const element = route.element;

  if (route.auth && isFvtOnlyProbandBlockedPath(route.path)) {
    return <FvtOnlyProbandRedirect>{element}</FvtOnlyProbandRedirect>;
  }

  return element;
}

function RouterProvider() {
  const [, dispatch] = useContext(Context);
  const { fvtReferralUUID, isLoadingUserInfo, isLoggedIn, onInvalidAuth } = useApp();
  const { keycloak, initialized } = useKeycloak();

  // Update store with keycloak instance when it's available
  useEffect(() => {
    if (keycloak && initialized) {
      dispatch({ type: 'SET_KEYCLOAK', payload: keycloak });
    }
  }, [keycloak, initialized, dispatch]);

  const getAppInfo = useCallback(async () => {
    try {
      if (!isLoggedIn) return;

      dispatch({ type: 'SET_USER_INFO_FETCHING', payload: true });
      dispatch({ type: 'SET_USER_INFO_LOADED', payload: false });

      const userId = await fetchUserID();

      if (userId) {
        dispatch({ type: 'SET_USER_ID', payload: userId });
        const [userInfo, testRequests, geneticCounselingDetails] = await Promise.allSettled([
          fetchUserInfo(userId),
          fetchUserTestRequests(),
          fetchGeneticCounselingDetails(userId),
        ]);
        if (userInfo.status === 'fulfilled') {
          dispatch({ type: 'SET_USER_INFO', payload: userInfo.value });
          // Identify user in OpenReplay
          const userInfoData = userInfo.value;
          const userName = userInfoData.name
            ? `${userInfoData.name.first || ''} ${userInfoData.name.last || ''}`.trim()
            : undefined;
          const userEmail = userInfoData.contact_info?.[0]?.email;
          identifyUser(userId, userName || undefined, userEmail);
        }
        if (testRequests.status === 'fulfilled') {
          dispatch({
            type: 'SET_ALL_TEST_REQUESTS',
            payload: testRequests.value || [],
          });
        }
        if (geneticCounselingDetails.status === 'fulfilled') {
          dispatch({
            type: 'SET_GENETIC_COUNSELING_DETAILS',
            payload: geneticCounselingDetails.value,
          });
          dispatch({
            type: 'SET_GC_ACCESS_CODE',
            payload: geneticCounselingDetails.value?.access_code,
          });
        }
      } else {
        onInvalidAuth();
      }
    } catch (err) {
      console.error('Error on get user information: ', err);
      const isAuthErrorSuppressed = SUPPRESSED_AUTH_ERROR_ROUTES.some(p => matchPath(p, window.location.pathname));
      if (!isAuthErrorSuppressed) {
        onInvalidAuth();
      }
    } finally {
      dispatch({ type: 'SET_USER_INFO_FETCHING', payload: false });
      if (isLoggedIn) {
        dispatch({ type: 'SET_USER_INFO_LOADED', payload: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    getAppInfo();
  }, [getAppInfo]);

  if (!initialized) {
    return <div style={{ color: '#003595', fontSize: '24px', fontWeight: '600', marginTop: '18px' }}>Loading...</div>;
  }

  // Routes that should NOT have the main layout (Header/Footer)
  const routesWithoutLayout = [ROUTES.ORDER, '/', '/login'];

  return (
    <Routes>
      {/* Redirect partnerships/:partner/* to partnership/:partner/* */}
      <Route path='partnerships/:partner/*' element={<RedirectPartnershipsToPartnership />} />

      {/* Literal partner route — must precede the parameterized `:partner` block below
          so the BridgeBio FVT HCP intake page wins the URL match. */}
      <Route path={ROUTES.BRIDGEBIO_REFERRAL} element={<HcpReferral />} />

      {/* Generic dynamic route for any partner (predefined or from API) */}
      <Route
        path={`${ROUTES.PARTNERSHIP}/:partner`}
        element={
          <OrderProvider>
            <OrderLayout />
          </OrderProvider>
        }
      >
        {getOrderRoutes(<OrderLaunch />)}
      </Route>

      {/* Non-partner routes: /order/* */}
      <Route
        path={ROUTES.ORDER}
        element={
          <OrderProvider>
            <OrderLayout />
          </OrderProvider>
        }
      >
        {getOrderRoutes(<RedirectToGetStarted />)}
        <Route
          path={ROUTES.ORDER_NEW_TEST}
          element={isLoggedIn ? <OrderNewTest /> : <Navigate to={ROUTES.LOGIN} replace={true} />}
        />
      </Route>

      {/* Routes with MainLayout (Header/Footer) */}
      <Route element={<MainLayout />}>
        {routes
          .filter(route => !routesWithoutLayout.includes(route.path))
          .map(route => (
            <Route key={route.path} path={route.path} element={renderMainLayoutRouteElement(route, isLoggedIn)} />
          ))}

        {/* Always mounted so a direct hit/refresh never falls through to the 404 catch-all
            while the profile loads; the guard owns the loading/redirect gate. */}
        <Route
          element={
            <FvtRouteGuard
              fvtReferralUUID={fvtReferralUUID}
              isLoggedIn={isLoggedIn}
              isLoadingUserInfo={isLoadingUserInfo}
            />
          }
        >
          <Route element={<ReferralRoot fvtReferralUUID={fvtReferralUUID} />}>
            <Route path={ROUTES.FVT_INVITE} element={<FvtInvite />} />
            <Route path={ROUTES.FVT_FAMILY} element={<FvtFamily />} />
          </Route>
        </Route>
      </Route>

      {/* Routes without MainLayout (Login, Order flow) */}
      {routes
        .filter(route => routesWithoutLayout.includes(route.path))
        .map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={
              !route.auth || isLoggedIn ? route.element : <Navigate to={route.redirect ?? '/login'} replace={true} />
            }
          />
        ))}
    </Routes>
  );
}

export default RouterProvider;
